import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, KeyRound } from "lucide-react";

const NAV = [
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/get-help", label: "Get Help" },
  { to: "/volunteer", label: "Volunteer" },
  { to: "/events", label: "Events" },
  { to: "/stories", label: "Stories" },
  { to: "/impact", label: "Impact" },
  { to: "/resources", label: "Resources" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-clay/15 bg-oat/95" : "border-transparent bg-oat"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 group">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-clay text-oat transition-transform duration-300 group-hover:-rotate-6">
            <KeyRound className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-xl font-semibold tracking-tight text-ink">KEYZ</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-clay">Foundation</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-semibold transition-colors duration-200 ${
                  isActive ? "bg-oat-100 text-clay" : "text-ink hover:text-clay"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/donate"
            data-testid="nav-donate-btn"
            className="hidden md:inline-flex items-center rounded-full bg-clay px-5 py-2.5 text-sm font-bold text-oat transition-colors duration-300 hover:bg-clay-600"
          >
            Donate
          </Link>
          <button
            type="button"
            aria-label="Open menu"
            data-testid="nav-mobile-toggle"
            className="grid h-10 w-10 place-items-center rounded-full border border-clay/20 text-ink lg:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-clay/15 bg-oat lg:hidden">
          <nav className="mx-auto grid max-w-7xl grid-cols-2 gap-1 px-6 py-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2 text-sm font-semibold ${
                    isActive ? "bg-oat-100 text-clay" : "text-ink hover:bg-oat-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/donate"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-donate"
              className="col-span-2 mt-2 inline-flex items-center justify-center rounded-full bg-clay px-5 py-3 text-sm font-bold text-oat"
            >
              Donate
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
